import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Document } from '@/types/vendor';

export interface DocumentUpload {
  id: string;
  file: File;
  type: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  uploadedDocument?: Document;
}

export interface DocumentsState {
  uploads: DocumentUpload[];
  documents: Document[];
  isUploading: boolean;
  error: string | null;
}

const initialState: DocumentsState = {
  uploads: [],
  documents: [],
  isUploading: false,
  error: null,
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    addUpload: (
      state,
      action: PayloadAction<{ id: string; file: File; type: string }>
    ) => {
      state.uploads.push({
        id: action.payload.id,
        file: action.payload.file,
        type: action.payload.type,
        progress: 0,
        status: 'pending',
      });
    },
    updateUploadProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) => {
      const upload = state.uploads.find((u) => u.id === action.payload.id);
      if (upload) {
        upload.progress = action.payload.progress;
        upload.status = 'uploading';
      }
    },
    uploadSuccess: (
      state,
      action: PayloadAction<{ id: string; document: Document }>
    ) => {
      const upload = state.uploads.find((u) => u.id === action.payload.id);
      if (upload) {
        upload.status = 'success';
        upload.progress = 100;
        upload.uploadedDocument = action.payload.document;
      }
      state.documents.push(action.payload.document);
    },
    uploadFailure: (
      state,
      action: PayloadAction<{ id: string; error: string }>
    ) => {
      const upload = state.uploads.find((u) => u.id === action.payload.id);
      if (upload) {
        upload.status = 'error';
        upload.error = action.payload.error;
      }
    },
    removeUpload: (state, action: PayloadAction<string>) => {
      state.uploads = state.uploads.filter((u) => u.id !== action.payload);
    },
    setDocuments: (state, action: PayloadAction<Document[]>) => {
      state.documents = action.payload;
    },
    addDocument: (state, action: PayloadAction<Document>) => {
      state.documents.push(action.payload);
    },
    removeDocument: (state, action: PayloadAction<string>) => {
      state.documents = state.documents.filter(
        (doc) => doc.documentId !== action.payload
      );
    },
    setUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
    setDocumentsError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearDocumentsError: (state) => {
      state.error = null;
    },
    clearUploads: (state) => {
      state.uploads = [];
    },
    resetDocuments: (state) => {
      state.uploads = [];
      state.documents = [];
      state.isUploading = false;
      state.error = null;
    },
  },
});

export const {
  addUpload,
  updateUploadProgress,
  uploadSuccess,
  uploadFailure,
  removeUpload,
  setDocuments,
  addDocument,
  removeDocument,
  setUploading,
  setDocumentsError,
  clearDocumentsError,
  clearUploads,
  resetDocuments,
} = documentsSlice.actions;

export default documentsSlice.reducer;
